import { injectable } from 'inversify';
import { action, computed, IReactionDisposer, makeObservable, observable, reaction } from 'mobx';
import { IConfiguration } from '../../../configuration/config.types';
import { NO_IMAGE_TO_DISPLAY_ERROR_CODES } from '../../helpers/global.constants';
import { ACDLError } from '../../models/error.model';
import { IApiService } from '../../services/api.service.base';
import { IImage, LOW_QUALITY_PRELOAD_MIN_IMAGE_SIZE, NoImageReason } from './annotations.interface';

export interface IQueueItem {
  image: IImage;
  reLockIntervalId?: number;
}

export interface IImagesQueueService {
  isAnythingToDisplay: boolean;
  areAnyImagesToLoad: boolean;
  noImageReason: NoImageReason;
  isAnyRequestInProgress: boolean;
  start(clearOnStart: boolean): void;
  takeImageFromQueue(): IImage | undefined;
  clear(): void;
  dispose(): void;
}

@injectable()
export class ImagesQueueServiceBase implements IImagesQueueService {
  protected readonly apiService: IApiService;
  protected setLoader: (enabled: boolean) => void;
  private getNewImageCallback?: () => Promise<IImage | ACDLError>;
  private refreshImageLockCallback?: (imageId: string) => Promise<void | ACDLError>;
  private refreshImageFailedCallback?: (imageId: string) => Promise<void>;
  private imageQueueMaxLength = 0;
  private queueSizeChangeReaction?: IReactionDisposer;
  protected takenItem?: IQueueItem;
  protected configuration: IConfiguration;

  constructor(configuration: IConfiguration, apiService: IApiService, setLoader: (enabled: boolean) => void) {
    makeObservable<ImagesQueueServiceBase, 'requestsInProgressCount' | 'setupBase' | 'loadImageToQueue' | 'assignProperNoImageReason'>(this, {
      areAnyImagesToLoad: observable,
      isAnythingToDisplay: computed,
      queue: observable,
      removeLocks: observable,
      noImageReason: observable,
      isAnyRequestInProgress: computed,
      requestsInProgressCount: observable,
      takeImageFromQueue: action,
      clear: action,
      dispose: action,
      start: action,
      setupBase: action,
      loadImageToQueue: action,
      assignProperNoImageReason: action,
    });

    this.apiService = apiService;
    this.setLoader = setLoader;
    this.configuration = configuration;
  }

  public get currentImageId(): string | undefined {
    return this.takenItem?.image.id;
  }

  public areAnyImagesToLoad: boolean = true;

  get isAnythingToDisplay(): boolean {
    return this.queue.length > 0;
  }

  public queue: IQueueItem[] = [];

  public removeLocks: boolean = true;

  public noImageReason: NoImageReason = NoImageReason.Unknown;

  get isAnyRequestInProgress(): boolean {
    return this.requestsInProgressCount > 0;
  }

  private requestsInProgressCount: number = 0;

  public takeImageFromQueue(): IImage | undefined {
    this.removeLockRefresh(this.takenItem);
    const item = this.queue.shift();

    if (item === undefined) {
      return undefined;
    }
    const oldItem = this.takenItem;
    this.takenItem = item;

    setTimeout(() => {
      if (oldItem) {
        URL.revokeObjectURL(oldItem.image.url);
        URL.revokeObjectURL(oldItem.image.lowQualityUrl);
      }
    }, 1000);

    return item.image;
  }

  public clear(): void {
    if (this.queueSizeChangeReaction) {
      this.queueSizeChangeReaction();
    }
    this.queue.forEach((i) => {
      this.removeLockRefresh(i);
      URL.revokeObjectURL(i.image.url);
      URL.revokeObjectURL(i.image.lowQualityUrl);
    });
    if (this.takenItem) {
      this.removeLockRefresh(this.takenItem);
      URL.revokeObjectURL(this.takenItem.image.url);
      URL.revokeObjectURL(this.takenItem.image.lowQualityUrl);
    }
    this.takenItem = undefined;
    this.areAnyImagesToLoad = true;
    this.queue = [];
  }

  public dispose(): void {
    if (this.queueSizeChangeReaction) {
      this.queueSizeChangeReaction();
    }
  }

  public start(clearOnStart: boolean): void {
    if (clearOnStart) {
      this.clear();
    }

    const handleReaction = async (): Promise<void> => {
      for (let i = 0; i < this.imageQueueMaxLength; i += 1) {
        if (this.requestsInProgressCount + this.queue.length + 1 <= this.imageQueueMaxLength && this.areAnyImagesToLoad) {
          try {
            this.setLoader(true);
            this.requestsInProgressCount += 1;
            // eslint-disable-next-line no-await-in-loop -- load images one by one
            await this.loadImageToQueue();
            this.removeLocks = false;
          } catch {
            if (this.queueSizeChangeReaction) {
              this.queueSizeChangeReaction();
            }
          } finally {
            this.requestsInProgressCount -= 1;
            if (this.requestsInProgressCount === 0) {
              this.setLoader(false);
            }
          }
        }
      }
    };

    this.queueSizeChangeReaction = reaction(
      () => this.queue.length,
      () => {
        void handleReaction();
      },
      {
        fireImmediately: true,
      }
    );
  }

  protected setupBase(
    getNewImageCallback: () => Promise<IImage | ACDLError>,
    imageQueueMaxLength: number,
    refreshLockCallback: (imageId: string) => Promise<void | ACDLError>,
    refreshImageFailedCallback: (imageId: string) => Promise<void>
  ): void {
    this.getNewImageCallback = getNewImageCallback;
    this.imageQueueMaxLength = imageQueueMaxLength;
    this.refreshImageLockCallback = refreshLockCallback;
    this.refreshImageFailedCallback = refreshImageFailedCallback;
  }

  private async loadImageToQueue(): Promise<boolean> {
    if (!this.getNewImageCallback) {
      return false;
    }
    if (!this.areAnyImagesToLoad) {
      return false;
    }

    const result = await this.getNewImageCallback();

    if (result instanceof ACDLError) {
      this.assignProperNoImageReason(result.apiErrorResponse?.errorCodes);
      this.areAnyImagesToLoad = false;
      return false;
    } else if (result.noImageReason) {
      this.assignProperNoImageReason([result.noImageReason]);
      this.areAnyImagesToLoad = false;
      return false;
    }

    if ((this.takenItem && this.takenItem.image.id === result.id) || this.queue.some((i) => i.image.id === result.id)) {
      return false;
    }

    const item = {
      image: observable({ ...result, url: '', lowQualityUrl: '' }),
    };

    this.addLockRefresh(item);

    void this.startLoading(item);

    this.queue.push(item);

    return true;
  }

  private assignProperNoImageReason(errorCodes?: string[]): void {
    if (!errorCodes) {
      this.noImageReason = NoImageReason.Unknown;
      return;
    }

    if (errorCodes.includes(NO_IMAGE_TO_DISPLAY_ERROR_CODES.NO_MORE_CREDITS)) {
      this.noImageReason = NoImageReason.Credits;
    } else if (
      errorCodes.includes(NO_IMAGE_TO_DISPLAY_ERROR_CODES.NO_MORE_IMAGES_IN_PROJECT) ||
      errorCodes.includes(NO_IMAGE_TO_DISPLAY_ERROR_CODES.NO_ANNOTATIONS_TO_REVIEW)
    ) {
      this.noImageReason = NoImageReason.NoMoreImages;
    } else if (
      errorCodes.includes(NO_IMAGE_TO_DISPLAY_ERROR_CODES.NO_MORE_UNLOCKED_IMAGES_IN_PROJECT) ||
      errorCodes.includes(NO_IMAGE_TO_DISPLAY_ERROR_CODES.NO_UNLOCKED_ANNOTATIONS_TO_REVIEW)
    ) {
      this.noImageReason = NoImageReason.Locked;
    } else {
      this.noImageReason = NoImageReason.QueueError;
    }
  }

  async startLoading(item: IQueueItem): Promise<void> {
    await Promise.all([this.loadLowQualityImage(item), this.loadImage(item)]);
  }

  async loadLowQualityImage(item: IQueueItem): Promise<void> {
    if (item.image.size < LOW_QUALITY_PRELOAD_MIN_IMAGE_SIZE) {
      return;
    }
    const imageFile = await this.apiService.getImageAsync(this.apiService.getUrl(`/Images/GetImageThumbnail/${item.image.id}`));
    if (!(imageFile instanceof ACDLError)) {
      item.image.lowQualityUrl = URL.createObjectURL(imageFile.blob);
    }
  }

  async loadImage(item: IQueueItem): Promise<void> {
    const imageFile = await this.apiService.getImageAsync(this.apiService.getUrl(`/Images/GetImage/${item.image.id}`));
    if (!(imageFile instanceof ACDLError)) {
      item.image.url = URL.createObjectURL(imageFile.blob);
    }
  }

  private addLockRefresh(item: IQueueItem): void {
    if (!item || !item.image || !item.image.id) {
      return;
    }

    item.reLockIntervalId =
      this.refreshImageLockCallback !== undefined
        ? window.setInterval(
            () => {
              void this.refreshImage(item.image.id);
            },
            this.configuration.appConfig.reLockInterval * 60 * 1000
          )
        : undefined;
  }

  private async refreshImage(imageId: string): Promise<void> {
    const result = await this.refreshImageLockCallback?.(imageId);
    if (result && result.isBadRequestWithCode(['IMAGE_ALREADY_LOCKED'])) {
      if (this.takenItem?.image.id === imageId) {
        this.removeLockRefresh(this.takenItem);
        this.takenItem = undefined;
      } else {
        const item = this.queue.find((x) => x.image.id === imageId);
        if (item) {
          this.removeLockRefresh(item);
          const index = this.queue.indexOf(item);
          if (index > -1) {
            this.queue.splice(index, 1);
          }
        }
      }
      await this.refreshImageFailedCallback?.(imageId);
    }
  }

  private removeLockRefresh(item?: IQueueItem): void {
    if (!item || !item.reLockIntervalId) {
      return;
    }

    window.clearInterval(item.reLockIntervalId);
  }
}
